/**
 * OnboardingWizard — Formulário multi-step de suitability e dados cadastrais
 * Usado em: App.jsx (fluxo do cliente) e AdminPanel.jsx (admin)
 */
import { useState } from "react";
import type { ThemeObject } from "./theme.js";
import type { OnboardingForm, SuitabilityScore } from "./onboarding/types.js";
import { Step0Identificacao } from "./onboarding/Step0Identificacao.jsx";
import { Step1Objetivos } from "./onboarding/Step1Objetivos.jsx";
import { Step2Patrimonial } from "./onboarding/Step2Patrimonial.jsx";
import { Step3Suitability } from "./onboarding/Step3Suitability.jsx";
import { Step4Juridico } from "./onboarding/Step4Juridico.jsx";
import { Step5Conclusao } from "./onboarding/Step5Conclusao.jsx";
import { STEPS } from "./onboarding/constants.js";

interface OnboardingWizardProps {
  clientId: string;
  clientName?: string;
  initialData?: Partial<OnboardingForm>;
  onSave?: (clientId: string, data: OnboardingForm & Record<string, unknown>) => Promise<void>;
  onClose?: () => void;
  T: ThemeObject;
}

function OnboardingWizard({
  clientId,
  clientName,
  initialData,
  onSave,
  onClose,
  T,
}: OnboardingWizardProps) {
  // Dynamic form fields extend OnboardingForm via [key: string]: unknown
  const empty = {
    nomeCompleto: "",
    cpf: "",
    dataNascimento: "",
    estadoCivil: "Solteiro",
    regimeBens: "",
    pactoAntenupcial: false,
    profissao: "",
    objetivos: [] as string[],
    metaCurto: "",
    metaLongo: "",
    ativoLiquidez: { val: 0, inst: "", jur: "Brasil" },
    ativoAcoes: { val: 0, inst: "", jur: "" },
    ativoExterior: { val: 0, inst: "", jur: "" },
    ativoFundos: { val: 0, inst: "", jur: "" },
    imoveis: { val: 0, titan: "PF", desc: "" },
    empresas: { val: 0, titan: "PF", desc: "" },
    emprestimos: [{ tipo: "Empréstimo", val: 0, taxa: 0, descricao: "" }],
    expNivel: 1,
    reacaoQueda: "moderado",
    investQualif: false,
    horizonte: "longo",
    holdingConst: false,
    testamento: false,
    declaraCBE: false,
    dependentes: [{ nome: "", parentesco: "", dataNasc: "" }],
    chkDIRPF: false,
    chkExtratos: false,
    chkContratoSocial: false,
    chkComprovanteRes: false,
    assinaturaCliente: false,
    assinaturaConsultor: false,
    status: "pendente",
  } satisfies Record<string, unknown>;

  const migrateData = (data?: Partial<OnboardingForm>): OnboardingForm => {
    if (!data) return empty as OnboardingForm;
    const merged = { ...empty, ...data } as OnboardingForm & Record<string, unknown>;
    const passivos = (merged as Record<string, unknown>).passivos as
      | { val: number; taxa?: number }
      | undefined;
    if (passivos && !merged.emprestimos) {
      merged.emprestimos =
        passivos.val > 0
          ? [{ tipo: "Empréstimo", val: passivos.val, taxa: passivos.taxa || 0, descricao: "" }]
          : [{ tipo: "Empréstimo", val: 0, taxa: 0, descricao: "" }];
      delete (merged as Record<string, unknown>).passivos;
    }
    return merged;
  };

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<OnboardingForm>(() => migrateData(initialData));
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const onChange = (key: string, value: unknown) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const calcScore = (f: OnboardingForm): SuitabilityScore => {
    const e = f.expNivel === 1 ? 3 : f.expNivel === 2 ? 7 : 10;
    const r = f.reacaoQueda === "conservador" ? 2 : f.reacaoQueda === "moderado" ? 5 : 8;
    const h = f.horizonte === "curto" ? 2 : f.horizonte === "medio" ? 4 : 7;
    const q = f.investQualif ? 5 : 0;
    const total = Math.min(e + r + h + q, 25);
    const perfil =
      total <= 7
        ? "Conservador"
        : total <= 13
          ? "Moderado"
          : total <= 19
            ? "Arrojado"
            : "Agressivo";
    return { score: total, max: 25, perfil };
  };

  const score = calcScore(form);
  const scoreColor =
    (
      {
        Conservador: "#16A34A",
        Moderado: "#D97706",
        Arrojado: "#7C3AED",
        Agressivo: "#DC2626",
      } as Record<string, string>
    )[score.perfil] || "#64748B";

  const handleSave = async (andClose = false) => {
    if (!form.nomeCompleto?.trim()) {
      setSaveErr("O nome completo é obrigatório para salvar.");
      return;
    }
    setSaving(true);
    setSaveErr(null);
    const s = calcScore(form);
    const filledSecs = [form.nomeCompleto, form.cpf, form.profissao].filter(Boolean).length;
    const status =
      filledSecs >= 3 && form.objetivos.length > 0
        ? form.assinaturaCliente && form.assinaturaConsultor
          ? "completo"
          : "parcial"
        : "pendente";
    try {
      await onSave?.(clientId, {
        ...form,
        scoreSuit: s.score,
        scoreMax: s.max,
        perfilResult: s.perfil,
        status,
        lastUpdate: new Date().toISOString().slice(0, 10),
      });
      if (andClose && onClose) onClose();
    } catch (e) {
      setSaveErr(`Erro ao salvar: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    const commonProps = { form, onChange, T };
    const stepProps = { ...commonProps, score, scoreColor };

    switch (step) {
      case 0:
        return <Step0Identificacao {...commonProps} />;
      case 1:
        return <Step1Objetivos {...commonProps} />;
      case 2:
        return <Step2Patrimonial {...commonProps} />;
      case 3:
        return <Step3Suitability {...stepProps} />;
      case 4:
        return <Step4Juridico {...commonProps} />;
      case 5:
        return <Step5Conclusao {...stepProps} />;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.65)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 1002,
        overflowY: "auto",
        padding: "20px 16px",
        fontFamily: "'Inter','Segoe UI',sans-serif",
      }}
    >
      <div
        style={{
          width: "min(700px,100%)",
          background: T.card,
          borderRadius: 16,
          boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
          marginBottom: 20,
        }}
      >
        <div
          style={{
            background: T.accent,
            padding: "20px 28px",
            borderRadius: "16px 16px 0 0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#fff" }}>
              📋 Kit de Onboarding & Suitability
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
              {clientName} · LPA Wealth Management
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.3)",
                color: "rgba(255,255,255,0.75)",
                borderRadius: 8,
                padding: "7px 14px",
                cursor: "pointer",
                fontSize: 13,
              }}
            >
              ✕
            </button>
          )}
        </div>

        <div
          style={{
            padding: "14px 28px 0",
            display: "flex",
            gap: 4,
            overflowX: "auto",
            flexWrap: "wrap",
          }}
        >
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              style={{
                flex: "0 0 auto",
                padding: "5px 11px",
                borderRadius: 20,
                border: "none",
                cursor: "pointer",
                fontSize: 11,
                fontWeight: 600,
                background: step === i ? T.accent : i < step ? T.successBg : T.bg,
                color: step === i ? "#fff" : i < step ? "#16A34A" : T.sub,
              }}
            >
              {i < step ? "✓ " : ""}
              {s.replace(/^\d+\. /, "")}
            </button>
          ))}
        </div>

        <div style={{ padding: "22px 28px 28px" }}>
          {renderStep()}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 24,
              paddingTop: 16,
              borderTop: `1px solid ${T.border}`,
            }}
          >
            {(step > 0 || onClose) && (
              <button
                onClick={
                  step === 0
                    ? () => {
                        if (form.nomeCompleto?.trim() && onClose) {
                          if (window.confirm("Há dados não salvos. Deseja sair sem salvar?"))
                            onClose();
                        } else if (onClose) onClose();
                      }
                    : () => setStep((s) => s - 1)
                }
                style={{
                  padding: "10px 20px",
                  borderRadius: 9,
                  border: `1px solid ${T.border}`,
                  background: T.bg,
                  color: T.text,
                  cursor: "pointer",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                {step === 0 ? "✕ Cancelar" : "← Anterior"}
              </button>
            )}
            <div
              style={{ display: "flex", gap: 10, flexDirection: "column", alignItems: "flex-end" }}
            >
              {saveErr && (
                <div
                  style={{
                    fontSize: 12,
                    color: "#DC2626",
                    fontWeight: 600,
                    padding: "4px 8px",
                    background: "#FEE2E2",
                    borderRadius: 6,
                    maxWidth: 280,
                    textAlign: "right",
                  }}
                >
                  ⚠️ {saveErr}
                </div>
              )}
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => handleSave(false)}
                  disabled={saving}
                  style={{
                    padding: "10px 18px",
                    borderRadius: 9,
                    border: `1px solid ${T.accent}`,
                    background: "transparent",
                    color: T.accent,
                    cursor: saving ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: 13,
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {saving ? "⏳ Salvando…" : "💾 Salvar Rascunho"}
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    onClick={() => setStep((s) => s + 1)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 9,
                      border: "none",
                      background: T.accent,
                      color: "#fff",
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    Próximo →
                  </button>
                ) : (
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving}
                    style={{
                      padding: "10px 20px",
                      borderRadius: 9,
                      border: "none",
                      background: saving ? "#9CA3AF" : "#16A34A",
                      color: "#fff",
                      cursor: saving ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    {saving ? "⏳ Finalizando…" : "✅ Finalizar Onboarding"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OnboardingWizard;
